// @flow

import Vue from 'vue'
import type { Order } from '../types/order'
import type { OrderCreated } from '../types/order-created'
import type { OrderInitiatedPayment } from '../types/order-initiated-payment'
import type { OrderStatus } from '../types/order-status'
import type { PaymentMethodsOrder } from '../types/payment-methods-order'
import type OrderApiClientInterface, { IbanValidationResult, OrderValidationStage } from './order-api-client-interface'
import type { AddressInfo } from '../types/address-info'
import type { PriceSpecification } from '../types/price-specification'
import type { PaymentSpecification } from '../types/payment-specification'
import type { ValidationErrorResult, ValidationResult } from '../types/validation-result'
import type { $AxiosError } from 'axios'
import ValidationError from './error/validation-error'
import { ORDER_VALIDATION_STAGE_CART } from './order-api-client-constants'
import type { OrderPurchase } from '../types/order-purchase'
import type { Subscription } from '../types/subscription'
import TimeoutError from './error/timeout-error'
import ApiError from './error/api-error'
import type { TokenProviderInterface } from '../token/token-provider-interface'
import type { SubscriptionReminderCancellationResult } from '../types/subscription-reminder-cancellation-result'
import ProductConfiguration from '../types/product-configuration'
import type { CombiDealDiscountSuggestions } from '../types/combi-deal-discount-suggestions'
import type { OrderSentResult } from '../types/order-sent-result'
import SessionIdStorageInterface from '../service/session-id-storage-interface'

class OrderApiClient implements OrderApiClientInterface {
  baseUrl: string
  tokenProvider: TokenProviderInterface
  sessionIdStorage: SessionIdStorageInterface

  constructor (baseUrl: string, tokenProvider: TokenProviderInterface, sessionIdStorage: SessionIdStorageInterface) {
    this.baseUrl = baseUrl
    this.tokenProvider = tokenProvider
    this.sessionIdStorage = sessionIdStorage
  }

  getClientOptions () {
    const headers = {}
    const token = this.tokenProvider.getToken()
    if (token) {
      headers['Authorization'] = `Bearer ${token}`
    }
    headers['X-SID'] = this.sessionIdStorage.getSessionId()
    return {
      headers,
      withCredentials: true,
      timeout: 10000
    }
  }

  static convertError (error: $AxiosError<Object>): Error {
    if (error.response && error.response.data && error.response.data.errors) {
      return new ValidationError(error.response.data.errors, error)
    }
    if (error.code === 'ECONNABORTED') {
      return new TimeoutError(error)
    }
    return new ApiError(error)
  }

  async doCall (method: 'get' | 'post', path: string, params: Object, options: Object): any {
    try {
      const response = await Vue.$http[method](
        this.baseUrl + path,
        params,
        { ...this.getClientOptions(), ...options }
      )
      return response.data
    } catch (error) {
      throw this.constructor.convertError(error)
    }
  }

  async doGet (path: string, params: Object, options: Object = {}): any {
    return this.doCall('get', path, params, options)
  }

  async doPost (path: string, params: Object, options: Object = {}): any {
    return this.doCall('post', path, params, options)
  }

  async getPriceSpecificationForOrder (order: Order): Promise<PriceSpecification> {
    return (await this.doPost('/price', { order: order })).priceSpecification
  }

  async getCombiDealDiscountSuggestionsForOrder (order: Order): Promise<CombiDealDiscountSuggestions> {
    return this.doPost('/combi-deal-discount-suggestions', { order: order })
  }

  async getPaymentSpecificationForOrder (order: Order): Promise<PaymentSpecification> {
    return (await this.doPost('/payment', { order: order })).paymentSpecification
  }

  async validateOrder (
    order: Order, stage: OrderValidationStage = ORDER_VALIDATION_STAGE_CART): Promise<ValidationResult> {
    return this.doPost('/validate/' + stage, { order: order })
  }

  async createOrder (order: Order): Promise<OrderCreated> {
    return this.doPost('/create', { order: order })
  }

  async updateOrder (
    order: Order): Promise<ValidationResult> {
    return this.doPost('/update', { order: order })
  }

  async initiatePayment (orderPurchase: OrderPurchase): Promise<OrderInitiatedPayment> {
    return this.doPost('/initiate-payment', orderPurchase, {timeout: 60000})
  }

  async getOrderStatus (orderId: string, orderToken: string): Promise<OrderStatus> {
    const params = { orderId: orderId, orderToken: orderToken }
    return this.doPost('/status', params)
  }

  async getPaymentMethodsOrder (order: Order, token: ?string): Promise<PaymentMethodsOrder> {
    return this.doPost('/payment/methods', { order: order })
  }

  async validateIban (iban: string): Promise<IbanValidationResult> {
    return this.doPost('/iban', { iban: iban })
  }

  async getAddressInformation (
    postalCode: string, houseNumber: string, houseNumberExtension: string): Promise<AddressInfo> {
    const params = {
      postcode: postalCode,
      houseNumber: houseNumber,
      houseNumberExtension: houseNumberExtension
    }
    return (await this.doPost('/postcode/address_info', params)).addressInfo
  }

  async getSubscriptionByToken (token: string): Promise<Subscription> {
    return this.doGet(`/subscription/${token}`, {})
  }

  async cancelSubscriptionReminders (token: string): Promise<SubscriptionReminderCancellationResult> {
    return this.doGet(`/subscription/${token}/cancel-reminders`, {})
  }

  async filterProductConfigurations (configurations: ProductConfiguration[], sale: string, engravedId: string) : Promise<ProductConfiguration[]|ValidationErrorResult> {
    return this.doPost('/restriction/filter', {
      'specialSale': {'slug': sale},
      'applicant': {'engravedId': engravedId},
      'productConfigurations': configurations.map((configuration: ProductConfiguration) => ({code: configuration.orderCode}))
    })
  }

  async sendInvoice (orderId: string, orderToken: string, emailAddress: string): Promise<OrderSentResult> {
    return this.doPost('/send-invoice', { orderId: orderId, orderToken: orderToken, emailAddress: emailAddress })
  }

  async getInvoice (orderId: string, orderToken: string): Promise<string> {
    return this.doPost('/get-invoice', { orderId: orderId, orderToken: orderToken }, { responseType: 'arraybuffer' })
  }
}

export default OrderApiClient



// WEBPACK FOOTER //
// ./src/order-api/order-api-client.js